// exportGateway.ts — Single funnel for chargeable exports.
//
// Every deliverable artifact (PDF panel book, DXF drawings, CSV materials list,
// print) goes through `gatedExport`. The gateway:
//   1. Generates a client-side idempotency key (UUID).
//   2. Calls the /spend edge function before doing the work.
//      - On 402 (no tokens) → opens the upsell modal via the registered handler
//        and returns { ok: false, reason: 'no_tokens' } so the caller bails out.
//   3. Runs the producer function (which actually generates the artifact).
//   4. Reports a SHA-256 file_hash to the audit log via a second /spend "stamp"
//      call (or via metadata on the original spend — current impl uses metadata).
//
// Native cv-format exports (.cvpanel / .cvsite / .cvp / .cvt) bypass this util
// entirely — they're the inter-app transport format and are intentionally free.

import { supabase, SUPABASE_FN_URL } from '../auth/supabase';

export type ChargeableArtifact = 'pdf' | 'dxf' | 'csv' | 'print';

export type GatewayResult<T> =
  | { ok: true; value: T; new_balance: number }
  | { ok: false; reason: 'no_tokens' | 'unauthorized' | 'network' | 'producer_error' | 'preflight'; message?: string };

export interface SpendResponse {
  ok: boolean;
  new_balance?: number;
  balance?: number;
  error?: string;
  replayed?: boolean;
}

type UpsellHandler = (reason: 'tokens') => void;
let upsellHandler: UpsellHandler | null = null;

export function registerUpsellHandler(fn: UpsellHandler): void { upsellHandler = fn; }
export function unregisterUpsellHandler(): void { upsellHandler = null; }

async function sha256Hex(bytes: ArrayBuffer | Uint8Array): Promise<string> {
  const buf = bytes instanceof Uint8Array ? bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) : bytes;
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

interface GatedExportOptions<T> {
  artifact: ChargeableArtifact;
  /** Optional pre-spend validation. Throws to reject the export before any token is charged.
   *  Use for caller-side invariants the producer would otherwise discover (no site, no valid panels, etc). */
  preflight?: () => void | Promise<void>;
  /** Produces the artifact. Should return both the result for the caller and a Uint8Array
   *  representing the bytes that hit disk (used for file_hash audit). */
  produce: () => Promise<{ value: T; bytes: Uint8Array }>;
}

export async function gatedExport<T>(opts: GatedExportOptions<T>): Promise<GatewayResult<T>> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    upsellHandler?.('tokens');
    return { ok: false, reason: 'unauthorized', message: 'Sign in to export' };
  }

  if (opts.preflight) {
    try {
      await opts.preflight();
    } catch (e) {
      return {
        ok: false,
        reason: 'preflight',
        message: e instanceof Error ? e.message : String(e),
      };
    }
  }

  const key = newIdempotencyKey();
  let spendRes: Response;
  try {
    spendRes = await fetch(`${SUPABASE_FN_URL}/spend`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        artifact: opts.artifact,
        idempotency_key: key,
        app: 'cv-web',
      }),
    });
  } catch (e) {
    return { ok: false, reason: 'network', message: e instanceof Error ? e.message : String(e) };
  }

  if (spendRes.status === 402) {
    upsellHandler?.('tokens');
    return { ok: false, reason: 'no_tokens' };
  }
  if (!spendRes.ok) {
    return { ok: false, reason: 'network', message: `spend HTTP ${spendRes.status}` };
  }

  const spendBody: SpendResponse = await spendRes.json();
  if (!spendBody.ok) {
    return { ok: false, reason: 'network', message: spendBody.error ?? 'spend failed' };
  }

  let produced: { value: T; bytes: Uint8Array };
  try {
    produced = await opts.produce();
  } catch (e) {
    // Producer failed AFTER charging the token. We don't auto-refund; if this
    // proves common we'll add a /refund endpoint that takes the idempotency_key.
    return {
      ok: false,
      reason: 'producer_error',
      message: e instanceof Error ? e.message : String(e),
    };
  }

  // Best-effort stamp the file_hash onto the audit row. Failure is non-fatal.
  try {
    const hash = await sha256Hex(produced.bytes);
    await fetch(`${SUPABASE_FN_URL}/spend`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        artifact: opts.artifact,
        file_hash: hash,
        idempotency_key: key,         // same key — replay returns the same row
        app: 'cv-web',
      }),
    });
  } catch { /* ignore — audit stamp is best-effort */ }

  return { ok: true, value: produced.value, new_balance: spendBody.new_balance ?? 0 };
}

/** Convenience wrapper: gate + build bytes + download blob in one call. */
export async function gatedDownload(opts: {
  artifact: ChargeableArtifact;
  filename: string;
  mimeType: string;
  preflight?: () => void | Promise<void>;
  build: () => Promise<Uint8Array> | Uint8Array;
}): Promise<GatewayResult<void>> {
  return gatedExport<void>({
    artifact: opts.artifact,
    preflight: opts.preflight,
    produce: async () => {
      const bytes = await opts.build();
      const blob = new Blob([bytes], { type: opts.mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = opts.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return { value: undefined, bytes };
    },
  });
}
